import { localeTag } from './i18n.js';
// Conversions between a wall-clock value typed in a <input type="datetime-local"> and an instant,
// for an explicit IANA zone. Intl is used instead of a date library to keep the bundle small.
export const zones = [
  'Africa/Abidjan',
  'Africa/Dakar',
  'Africa/Douala',
  'Africa/Kinshasa',
  'Africa/Lagos',
  'Africa/Casablanca',
  'Africa/Nairobi',
  'Europe/Paris',
  'UTC',
];
export const browserZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'Africa/Abidjan';
function parts(d: Date, zone: string) {
  const out: Record<string, number> = {};
  for (const p of new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(d))
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  return out;
}
function offsetMs(d: Date, zone: string) {
  const p = parts(d, zone);
  return (
    Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second) -
    Math.floor(d.getTime() / 1000) * 1000
  );
}
export function localToIso(local: string, zone: string) {
  if (!local) return '';
  const guess = new Date(local.length === 16 ? `${local}:00Z` : `${local}Z`);
  if (Number.isNaN(guess.getTime())) return '';
  let t = guess.getTime() - offsetMs(guess, zone);
  // A second pass settles values that fall on a daylight-saving transition.
  t = guess.getTime() - offsetMs(new Date(t), zone);
  return new Date(t).toISOString();
}
/**
 * Like localToIso, but refuses a wall-clock time that does not exist in the zone, such as 02:30
 * on the day clocks move forward. A departure must never be shifted silently by an hour.
 * In the repeated hour of the autumn change, the later occurrence is used: it never leaves early.
 */
export function exactLocalToIso(local: string, zone: string) {
  const iso = localToIso(local, zone);
  return iso && isoToLocal(iso, zone) === local.slice(0, 16) ? iso : '';
}
const pad = (n: number) => String(n).padStart(2, '0');
export function isoToLocal(iso: string, zone: string) {
  const p = parts(new Date(iso), zone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour % 24)}:${pad(p.minute)}`;
}
export function formatInZone(iso: string, zone: string) {
  return new Intl.DateTimeFormat(localeTag(), {
    timeZone: zone,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}
/** Local wall-clock string of now plus `minutes`, suited to a datetime-local input. */
export function localFromNow(minutes: number, zone = browserZone()) {
  return isoToLocal(new Date(Date.now() + minutes * 60_000).toISOString(), zone);
}
